import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, IconButton, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography, useTheme, CircularProgress } from "@mui/material";
import { IconChevronDown, IconChevronUp, IconCopy, IconFile, IconBulb, IconMail, IconMessage, IconMoodSmileFilled, IconSend, IconTrash, IconUpload, IconX, IconPhoto } from "@tabler/icons-react";
import { convertDate, getStringHash, handleUpload, idGenerator } from "../util/helpers";
import { useEffect, useState, useContext, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { fetchGet, fetchPost, fetchDelete, transformFirebaseTime } from "../util/helpers";
import UserContext from "../contexts/UserContext";
import { FileViewer } from "../components";
import toast from "react-hot-toast";

export default function CreatorWorkstreamTab({ campaign }) {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState("chat");
    const [reviewContentOpen, setReviewContentOpen] = useState(false);
    const { campaignId } = useParams();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const { user } = useContext(UserContext);
    const [briefs, setBriefs] = useState([]);
    const chatEndRef = useRef(null);
    const [concept, setConcept] = useState("");
    const [deliverable, setDeliverable] = useState({});
    const [myDeliverables, setMyDeliverables] = useState([]);
    const [approveContent, setApproveContent] = useState({ approved: false });
    const [approveConcept, setApproveConcept] = useState({ approved: false });
    const [feedbackList, setFeedbackList] = useState([]);
    const [products, setProducts] = useState([]);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deliverableToDelete, setDeliverableToDelete] = useState(null);
    
    // New state for concept submission dialog
    const [conceptDialogOpen, setConceptDialogOpen] = useState(false);
    const [newConcept, setNewConcept] = useState("");
    const [conceptSubmitting, setConceptSubmitting] = useState(false);
    
    // New state for content upload dialog
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadCaption, setUploadCaption] = useState("");
    const [uploadPreview, setUploadPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const uploadFileRef = useRef(null);

    const tabs = [
        { name: "Chat", value: "chat" },
        { name: "Documents", value: "documents" },
        { name: "My Content", value: "my-content" },
        { name: "Affiliate", value: "affiliate" },
    ];

    const CONTENT_STATUS_TO_LABEL = {
        approved: "Approved",
        pending: "Pending",
    };

    const CONTENT_STATUS_TO_COLOR = {
        pending: { backgroundColor: theme.palette.info.light, color: theme.palette.info.main },
        approved: { backgroundColor: theme.palette.success.light, color: theme.palette.success.main }
    };

    const handleFetchMessages = async () => {
        const messages = await fetchGet(
            `/chat/campaigns/${campaignId}?creatorUserDocId=${user.userDocId}`,
        );
        setMessages(messages.result);
    };

    useEffect(() => {
        if (campaignId && user.userDocId) {
            handleFetchMessages();
            getBrandUploadedDocuments();
            getMyDeliverables();
            fetchProducts();
            
            // Poll for new messages every 5 seconds
            const messageInterval = setInterval(() => {
                handleFetchMessages();
            }, 5000);
            
            return () => clearInterval(messageInterval);
        }
    }, [campaignId, user.userDocId]);

    // Scroll to bottom when messages change
    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const sendMessage = async () => {
        if (newMessage.trim().length === 0 || !campaignId) {
            return;
        }
        await fetchPost("/chat/campaigns", {
            message: newMessage,
            sender: user.userDocId,
            campaignId: campaignId,
            recipient: campaign.userDocId, // Send to brand (campaign.userDocId is the brand's userDocId)
            sendingTime: new Date()
        });
        setNewMessage("");
        await handleFetchMessages();
    };

    const getBrandUploadedDocuments = async () => {
        const results = await fetchGet(
            `/brand/deliverables?campaignId=${campaignId}`,
        );
        if (results.result) {
            const tempFeedbackList = [];
            const tempBriefs = [];
            results.result.forEach((item) => {
                if (item.platform === "creator-brief") {
                    tempBriefs.push(item);
                }
                // Check for feedback directed at this creator
                if (item.creatorId === user.userDocId) {
                    if (item.platform === "Approve-Content") setApproveContent({ ...item });
                    else if (item.platform === "Approve-Concept") setApproveConcept({ ...item });
                    else if (item.platform === "Creator-Feedback") {
                        tempFeedbackList.push({ ...item, submittedAt: new Date(item.submittedAt._seconds * 1000 + item.submittedAt._nanoseconds / 1000000) });
                    }
                }
            });
            tempFeedbackList.sort((a, b) => b.submittedAt - a.submittedAt);
            setFeedbackList([...tempFeedbackList]);
            setBriefs([...tempBriefs]);
        }
    };

    const getMyDeliverables = async () => {
        const results = await fetchGet(
            `/brand/deliverables?campaignId=${campaignId}&deliveredBy=${user.userDocId}`,
        );

        if (results.result) {
            const resultDeliverables = [];
            results.result.forEach((item) => {
                if (item.platform === "concept") {
                    setConcept(item.link);
                } else if (item.platform === "draft") {
                    resultDeliverables.push({ ...item });
                }
            });
            resultDeliverables.sort((a, b) => transformFirebaseTime(b.submittedAt) - transformFirebaseTime(a.submittedAt));
            setDeliverable(resultDeliverables[0]);
            setMyDeliverables(resultDeliverables);
        }
    };

    const handleDeleteDocument = async () => {
        if (!deliverableToDelete) return;
        
        try {
            await fetchDelete(`/creator/deliverables/${deliverableToDelete.id}`);
            toast.success("Document deleted successfully");
            setDeleteConfirmOpen(false);
            setDeliverableToDelete(null);
            await getMyDeliverables();
        } catch (error) {
            toast.error("Failed to delete document");
        }
    };

    const handleUploadContent = async (url, type, size, fileType, fileName, caption = "") => {
        return fetchPost(`/brand/deliverables`, {
            campaignId: campaignId,
            platform: type,
            link: url,
            size: size,
            fileType: fileType,
            fileName: fileName.split(".")[0],
            deliveredBy: user.userDocId,
            caption: caption
        });
    };

    // Submit concept function
    const handleSubmitConcept = async () => {
        if (!newConcept.trim()) {
            toast.error("Please enter a concept description");
            return;
        }
        
        setConceptSubmitting(true);
        try {
            await fetchPost(`/brand/deliverables`, {
                campaignId: campaignId,
                platform: "concept",
                link: newConcept.trim(),
                deliveredBy: user.userDocId
            });
            toast.success("Concept submitted successfully!");
            setConcept(newConcept.trim());
            setNewConcept("");
            setConceptDialogOpen(false);
            await getMyDeliverables();
        } catch (error) {
            toast.error("Failed to submit concept");
        } finally {
            setConceptSubmitting(false);
        }
    };

    // Handle file selection for upload dialog
    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setUploadFile(file);
            // Create preview for images/videos
            if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
                const url = URL.createObjectURL(file);
                setUploadPreview(url);
            } else {
                setUploadPreview(null);
            }
        }
    };

    // Handle content upload with caption
    const handleContentUpload = async () => {
        if (!uploadFile) {
            toast.error("Please select a file to upload");
            return;
        }
        
        setUploading(true);
        try {
            const fileId = idGenerator();
            const fileExtension = uploadFile.name.split('.').pop();
            const url = await handleUpload(
                uploadFile,
                "draft",
                `campaign-${campaignId}-creator-${user.userDocId}-${fileId}.${fileExtension}`,
            );
            await handleUploadContent(url, "draft", uploadFile.size, uploadFile.type, uploadFile.name, uploadCaption);
            toast.success("Content uploaded successfully!");
            
            // Reset and close dialog
            setUploadFile(null);
            setUploadCaption("");
            setUploadPreview(null);
            setUploadDialogOpen(false);
            
            await getMyDeliverables();
        } catch (error) {
            toast.error("Failed to upload content");
        } finally {
            setUploading(false);
        }
    };

    // Close upload dialog and cleanup
    const handleCloseUploadDialog = () => {
        if (uploadPreview) {
            URL.revokeObjectURL(uploadPreview);
        }
        setUploadFile(null);
        setUploadCaption("");
        setUploadPreview(null);
        setUploadDialogOpen(false);
    };

    const fetchProducts = async () => {
        if (campaignId && user.userDocId) {
            const results = await fetchGet(
                `/product/redirect?campaignId=${campaignId}&creatorUserDocId=${user.userDocId}`,
            );
            setProducts(results.result || []);
        }
    };

    const userColor = useMemo(() => {
        return `../gradient${getStringHash(user?.name, 10)}.png`;
    }, [user?.name]);

    const brandColor = useMemo(() => {
        return `../gradient${getStringHash(campaign?.brand || "Brand", 10)}.png`;
    }, [campaign?.brand]);

    return (
        <Stack direction="column" spacing={2} sx={{ padding: "3%" }}>
            <Stack direction="row" sx={{ backgroundColor: "white", borderRadius: "var(--radius-lg)", minHeight: "600px" }}>
                <Stack direction="column" sx={{ paddingTop: "20px", width: "100%" }}>
                    <Stack direction="column" spacing={2} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Stack direction="row" justifyContent="space-between" sx={{ paddingX: "20px" }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <img className="profile-image" src={campaign?.image || brandColor} alt={campaign?.brand || "Brand"} />
                                <Stack direction="column" spacing={0}>
                                    <Typography variant="bodyregm" color="var(--main-text-color)">{campaign?.brand || "Brand"}</Typography>
                                    <Typography variant="bodysm" color="var(--body-text-color-600)">{campaign?.name}</Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button href={`mailto:${campaign?.contactEmail || ""}`} variant="outlined" className="outlined-button" color="info" startIcon={<IconMail className="icon-colored" size="18" color={theme.palette.info.main} />}>Contact Brand</Button>
                                <Button 
                                    onClick={() => {
                                        navigator.clipboard.writeText(campaign?.contactEmail || '');
                                        toast.success('Email copied to clipboard!');
                                    }} 
                                    variant="outlined" 
                                    className="outlined-button" 
                                    color="info" 
                                    startIcon={<IconCopy className="icon-colored" size="18" color={theme.palette.info.main} />}
                                >
                                    Copy Email
                                </Button>
                            </Stack>
                        </Stack>
                        <Tabs value={tabValue} onChange={(e, value) => setTabValue(value)} indicatorColor="secondary">
                            {tabs.map((tab) => (
                                <Tab key={tab.value} className={tabValue === tab.value ? "gradient-text" : ""} label={tab.name} value={tab.value} />
                            ))}
                        </Tabs>
                    </Stack>
                    {tabValue === "chat" &&
                        <Stack direction="column" sx={{ justifyContent: "end", height: "100%" }}>
                            <Box sx={{ display: "block", flexDirection: "column", justifyContent: "end", height: "345px", overflowY: "auto" }}>
                                {
                                    messages.map((messageData, index) => {
                                        return (
                                            <Stack key={index} direction="row" spacing={2} sx={{ padding: "10px", width: "100%", justifyContent: "end" }}>
                                                {messageData.sender !== user.userDocId && <img className="profile-image-small" src={brandColor} alt="Brand" />}
                                                <Stack direction="column" spacing={0.5} width="100%" alignItems={`${messageData.sender === user.userDocId && "end"}`}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography variant="bodyxs" color="var(--main-text-color)">{messageData.sender === user.userDocId ? "You" : campaign?.brand || "Brand"}</Typography>
                                                        <Typography variant="bodyxs" color="var(--body-text-color-200)">{transformFirebaseTime(messageData.submittedAt).toLocaleTimeString('en-US', {
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            hour12: true
                                                        })}</Typography>
                                                    </Stack>
                                                    <Box className={`chat-bubble-${messageData.sender !== user.userDocId ? "partner" : "user"}`}>
                                                        <Typography variant="bodysm" color={`${messageData.sender !== user.userDocId ? "var(--main-text-color)" : "white"}`}> {messageData.message}</Typography>
                                                    </Box>
                                                </Stack>
                                                {messageData.sender === user.userDocId && <img className="profile-image-small" src={userColor} alt="You" />}
                                            </Stack>
                                        );
                                    })
                                }
                                <div ref={chatEndRef} />
                            </Box>
                            <Divider />
                            <Stack direction="row" sx={{ padding: "10px" }}>
                                <TextField
                                    id="message-input"
                                    placeholder="Type your message here..."
                                    multiline
                                    maxRows={4}
                                    variant="standard"
                                    fullWidth
                                    sx={{ overflowY: "scroll" }}
                                    color="secondary"
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                />
                            </Stack>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ padding: "10px" }}>
                                <IconButton>
                                    <IconMoodSmileFilled className="icon-colored" size="20" color={theme.palette.info.main} />
                                </IconButton>
                                <Button onClick={() => sendMessage()} variant="contained" className="gradient-button" color="info" startIcon={<IconSend className="icon" size="18" />}>Send</Button>
                            </Stack>
                        </Stack>
                    }
                    {tabValue === "documents" && <Stack direction="column" spacing={2} padding="20px" height="100%">
                        <Typography variant="h4" color="var(--main-text-color)">Campaign Documents</Typography>
                        <Box sx={{ display: "block", flexDirection: "column", justifyContent: "end", height: "345px", overflowY: "auto" }}>
                            <Stack direction="column" sx={{ border: 1, borderColor: theme.palette.divider, borderRadius: "var(--radius-lg)" }}>
                                {briefs.length === 0 && (
                                    <Stack direction="column" spacing={2} padding="20px" alignItems="center">
                                        <Typography variant="bodysm" color="var(--body-text-color-600)">No documents available yet</Typography>
                                    </Stack>
                                )}
                                {briefs.map((briefData, index) => (
                                    <Stack key={index} direction="column">
                                        <Stack direction="row" spacing={2} sx={{ padding: "10px" }} alignItems="center">
                                            <IconFile className="icon-colored" size="30px" color="var(--body-text-color-500)" />
                                            <Stack direction="column">
                                                <Typography variant="bodyreg" color="var(--main-text-color)">{briefData.fileName || `Document ${index}`}</Typography>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="bodysm" color="var(--body-text-color-200)">{briefData.fileType || "Document"} Type</Typography>
                                                    <Box className="separator-dot" sx={{ backgroundColor: theme.palette.info.light }} />
                                                    <Typography variant="bodysm" color="var(--body-text-color-200)">{briefData.fileSize || ""}</Typography>
                                                </Stack>
                                            </Stack>
                                            {briefData.link && (
                                                <Button href={briefData.link} target="_blank" variant="outlined" className="outlined-button" color="info" size="small">View</Button>
                                            )}
                                        </Stack>
                                        {index < briefs.length - 1 && <Divider />}
                                    </Stack>
                                ))}
                            </Stack>
                        </Box>
                    </Stack>}
                    {tabValue === "my-content" && <Stack direction="column" spacing={2} padding="20px" height="100%">
                        <Typography variant="h4" color="var(--main-text-color)">My Content</Typography>
                        <Stack direction="column" spacing={1}>
                            <Stack direction="column" spacing={1} className="content-status-box">
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <IconMessage className="icon-colored" size="15px" color="var(--body-text-color-500)" />
                                        <Typography variant="bodyxs" color="var(--body-text-color-500)">Feedback: {feedbackList.length}</Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="bodyxs" color="var(--body-text-color-200)">Concept Status:</Typography>
                                        <Chip size="small" sx={CONTENT_STATUS_TO_COLOR[`${approveConcept.approved ? "approved" : "pending"}`]} label={CONTENT_STATUS_TO_LABEL[`${approveConcept.approved ? "approved" : "pending"}`]} />
                                        <Typography variant="bodyxs" color="var(--body-text-color-200)">Content Status:</Typography>
                                        <Chip size="small" sx={CONTENT_STATUS_TO_COLOR[`${approveContent.approved ? "approved" : "pending"}`]} label={CONTENT_STATUS_TO_LABEL[`${approveContent.approved ? "approved" : "pending"}`]} />
                                    </Stack>
                                </Stack>
                                {reviewContentOpen && <Stack direction="column" spacing={2}>
                                    <Divider />
                                    <Stack direction="row" spacing={4}>
                                        <Stack direction="column" spacing={1} maxWidth="50%">
                                            <Typography variant="bodysm" color="var(--main-text-color)">Content Preview</Typography>
                                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                                <Box sx={{ height: "300px", minWidth: "170px", backgroundColor: "var(--neutral-100)", borderRadius: "var(--radius-lg)", border: 1, borderColor: "var(--neutral-200)", overflow: "hidden" }}>
                                                    {deliverable && deliverable.link ? (
                                                        <FileViewer url={deliverable.link} mimeType={deliverable.fileType} />
                                                    ) : (
                                                        <Stack direction="column" spacing={1} alignItems="center" justifyContent="center" height="100%">
                                                            <IconPhoto className="icon-colored" size="40" color="var(--body-text-color-300)" />
                                                            <Typography variant="bodyxs" color="var(--body-text-color-400)">No content uploaded yet</Typography>
                                                        </Stack>
                                                    )}
                                                </Box>
                                                <Stack direction="column" spacing={1} justifyContent="space-between" height="100%">
                                                    <Stack direction="column" spacing={0.5} sx={{ overflowY: "auto" }}>
                                                        <Typography variant="bodyxs" color="var(--main-text-color)">Caption</Typography>
                                                        <Typography variant="bodyxs" color="var(--body-text-color-500)">
                                                            {deliverable && deliverable.caption ? deliverable.caption : "No caption provided"}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        </Stack>
                                        <Stack direction="column" spacing={3}>
                                            <Stack direction="column" spacing={1}>
                                                <Typography variant="bodysm" color="var(--main-text-color)">Concept</Typography>
                                                <Typography variant="bodyxs" color="var(--body-text-color-500)" sx={{ whiteSpace: "pre-wrap" }}>{concept || "No concept submitted yet"}</Typography>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                </Stack>}
                                <Stack direction="row">
                                    <Button color="info" startIcon={reviewContentOpen ? <IconChevronUp className="icon-colored" size="18" color="var(--body-text-color-500)" /> : <IconChevronDown className="icon-colored" size="18" color="var(--body-text-color-500)" />}
                                        onClick={() => setReviewContentOpen(!reviewContentOpen)}>{reviewContentOpen ? "Close" : "View My Content"}</Button>
                                </Stack>
                            </Stack>
                        </Stack>
                        
                        {/* Feedback from Brand */}
                        <Stack direction="column" spacing={1} sx={{ border: 1, borderColor: theme.palette.divider, borderRadius: "var(--radius-lg)", padding: "16px" }}>
                            <Typography variant="bodyregm" color="var(--main-text-color)">Feedback from Brand</Typography>
                            {feedbackList.length === 0 && (
                                <Typography variant="bodysm" color="var(--body-text-color-500)">No feedback yet</Typography>
                            )}
                            {feedbackList.map((data, i) => (
                                <Stack key={i} direction="column" spacing={0.5}>
                                    <Typography variant="bodysm" color="var(--body-text-color-500)">{data.feebback}</Typography>
                                    <Typography variant="bodyxs" color="var(--body-text-color-300)">
                                        {convertDate(data.submittedAt)}
                                    </Typography>
                                    {feedbackList.length !== i + 1 && <Divider />}
                                </Stack>
                            ))}
                        </Stack>

                        {/* Action Buttons */}
                        <Stack direction="row" spacing={2}>
                            <Button 
                                variant="outlined" 
                                className="outlined-button" 
                                color="info" 
                                startIcon={<IconBulb className="icon-colored" size="18" color={theme.palette.info.main} />} 
                                onClick={() => {
                                    setNewConcept(concept || "");
                                    setConceptDialogOpen(true);
                                }}
                            >
                                {concept ? "Edit Concept" : "Submit Concept"}
                            </Button>
                            <Button 
                                variant="contained" 
                                className="gradient-button" 
                                color="info" 
                                startIcon={<IconUpload className="icon" size="18" />} 
                                onClick={() => setUploadDialogOpen(true)}
                            >
                                Upload Content
                            </Button>
                        </Stack>
                        
                        {/* My Uploaded Documents List */}
                        {myDeliverables.length > 0 && (
                            <Stack direction="column" spacing={2} sx={{ marginTop: 3 }}>
                                <Typography variant="bodyregm" color="var(--main-text-color)">My Uploaded Documents</Typography>
                                <Stack direction="column" sx={{ border: 1, borderColor: theme.palette.divider, borderRadius: "var(--radius-lg)" }}>
                                    {myDeliverables.map((doc, index) => (
                                        <Stack key={doc.id} direction="column">
                                            <Stack direction="row" spacing={2} sx={{ padding: "10px" }} alignItems="center" justifyContent="space-between">
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <IconFile className="icon-colored" size="30px" color="var(--body-text-color-500)" />
                                                    <Stack direction="column">
                                                        <Typography variant="bodyreg" color="var(--main-text-color)">{doc.fileName || `Document ${index + 1}`}</Typography>
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <Typography variant="bodyxs" color="var(--body-text-color-200)">{doc.fileType || "Document"}</Typography>
                                                            <Box className="separator-dot" sx={{ backgroundColor: theme.palette.info.light }} />
                                                            <Typography variant="bodyxs" color="var(--body-text-color-200)">{convertDate(transformFirebaseTime(doc.submittedAt))}</Typography>
                                                        </Stack>
                                                    </Stack>
                                                </Stack>
                                                <Stack direction="row" spacing={1}>
                                                    {doc.link && (
                                                        <IconButton 
                                                            size="small"
                                                            onClick={() => window.open(doc.link, '_blank')}
                                                            title="View document"
                                                        >
                                                            <IconFile className="icon-colored" size="18" color={theme.palette.info.main} />
                                                        </IconButton>
                                                    )}
                                                    <IconButton 
                                                        size="small"
                                                        color="error"
                                                        onClick={() => {
                                                            setDeliverableToDelete(doc);
                                                            setDeleteConfirmOpen(true);
                                                        }}
                                                        title="Delete document"
                                                    >
                                                        <IconTrash size="18" />
                                                    </IconButton>
                                                </Stack>
                                            </Stack>
                                            {index < myDeliverables.length - 1 && <Divider />}
                                        </Stack>
                                    ))}
                                </Stack>
                            </Stack>
                        )}
                    </Stack>}
                    {tabValue === "affiliate" && <Stack direction="column" spacing={2} padding="20px" height="100%">
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="h4" color="var(--main-text-color)">My Affiliate Links</Typography>
                        </Stack>
                        <Stack direction="column" spacing={2}>
                            {products.length === 0 ? (
                                <Typography variant="bodysm" color="var(--body-text-color-600)">No affiliate links available yet. The brand will provide these to you.</Typography>
                            ) : (
                                <TableContainer>
                                    <Table sx={{ minWidth: 650 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    <Typography variant="bodysm" color="var(--body-text-color-600)">
                                                        Your Tracking Link
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="bodysm" color="var(--body-text-color-600)">
                                                        Conversions
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="bodysm" color="var(--body-text-color-600)">
                                                        Product URL
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {products.map((product, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <Typography variant="body2" color="var(--main-text-color)">{`${window.location.origin}/product/${product.id}`}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="var(--main-text-color)">{product.visits || 0}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="var(--main-text-color)">{product.productURL}</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Stack>
                    </Stack>}
                </Stack>
            </Stack>
            
            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => {
                    setDeleteConfirmOpen(false);
                    setDeliverableToDelete(null);
                }}
            >
                <DialogTitle>Delete Document</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete "{deliverableToDelete?.fileName}"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => {
                            setDeleteConfirmOpen(false);
                            setDeliverableToDelete(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteDocument} 
                        color="error" 
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Concept Submission Dialog */}
            <Dialog
                open={conceptDialogOpen}
                onClose={() => setConceptDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <IconBulb className="icon-colored" size="24" color={theme.palette.info.main} />
                        <Typography variant="h6">{concept ? "Edit Your Concept" : "Submit Your Concept"}</Typography>
                    </Stack>
                    <IconButton onClick={() => setConceptDialogOpen(false)} size="small">
                        <IconX size="18" />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Stack direction="column" spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="bodysm" color="var(--body-text-color-600)">
                            Describe your content concept or idea for this campaign. Be specific about your approach, messaging, and creative direction.
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={6}
                            placeholder="Example: I plan to create a 60-second TikTok video showcasing the product in my morning routine. I'll highlight the key benefits and include a call-to-action for viewers to check out the link in my bio..."
                            value={newConcept}
                            onChange={(e) => setNewConcept(e.target.value)}
                            color="secondary"
                            variant="outlined"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ padding: "16px 24px" }}>
                    <Button 
                        onClick={() => setConceptDialogOpen(false)}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSubmitConcept}
                        variant="contained"
                        className="gradient-button"
                        disabled={conceptSubmitting || !newConcept.trim()}
                        startIcon={conceptSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {conceptSubmitting ? "Submitting..." : (concept ? "Update Concept" : "Submit Concept")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Content Upload Dialog */}
            <Dialog
                open={uploadDialogOpen}
                onClose={handleCloseUploadDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <IconUpload className="icon-colored" size="24" color={theme.palette.info.main} />
                        <Typography variant="h6">Upload Content</Typography>
                    </Stack>
                    <IconButton onClick={handleCloseUploadDialog} size="small">
                        <IconX size="18" />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Stack direction="column" spacing={3} sx={{ mt: 1 }}>
                        {/* File Upload Area */}
                        <Stack direction="column" spacing={1}>
                            <Typography variant="bodyregm" color="var(--main-text-color)">Content File *</Typography>
                            <input 
                                type="file" 
                                ref={uploadFileRef} 
                                style={{ display: "none" }}
                                accept="image/*,video/*"
                                onChange={handleFileSelect}
                            />
                            {!uploadFile ? (
                                <Box
                                    onClick={() => uploadFileRef.current?.click()}
                                    sx={{
                                        border: "2px dashed",
                                        borderColor: theme.palette.divider,
                                        borderRadius: "var(--radius-lg)",
                                        padding: "40px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        backgroundColor: "var(--neutral-50)",
                                        "&:hover": {
                                            borderColor: theme.palette.info.main,
                                            backgroundColor: "var(--neutral-100)"
                                        }
                                    }}
                                >
                                    <IconUpload size="40" color="var(--body-text-color-400)" />
                                    <Typography variant="bodysm" color="var(--body-text-color-600)" sx={{ mt: 1 }}>
                                        Click to upload your content
                                    </Typography>
                                    <Typography variant="bodyxs" color="var(--body-text-color-400)">
                                        Supports images and videos
                                    </Typography>
                                </Box>
                            ) : (
                                <Stack direction="column" spacing={2}>
                                    {/* Preview */}
                                    {uploadPreview && (
                                        <Box sx={{ 
                                            width: "100%", 
                                            maxHeight: "200px", 
                                            borderRadius: "var(--radius-lg)", 
                                            overflow: "hidden",
                                            backgroundColor: "var(--neutral-100)"
                                        }}>
                                            {uploadFile.type.startsWith('image/') ? (
                                                <img 
                                                    src={uploadPreview} 
                                                    alt="Preview" 
                                                    style={{ 
                                                        width: "100%", 
                                                        maxHeight: "200px", 
                                                        objectFit: "contain" 
                                                    }} 
                                                />
                                            ) : (
                                                <video 
                                                    src={uploadPreview} 
                                                    style={{ 
                                                        width: "100%", 
                                                        maxHeight: "200px" 
                                                    }} 
                                                    controls 
                                                />
                                            )}
                                        </Box>
                                    )}
                                    {/* File info */}
                                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <IconFile size="20" color="var(--body-text-color-500)" />
                                            <Stack direction="column">
                                                <Typography variant="bodysm" color="var(--main-text-color)">
                                                    {uploadFile.name}
                                                </Typography>
                                                <Typography variant="bodyxs" color="var(--body-text-color-400)">
                                                    {(uploadFile.size / (1024 * 1024)).toFixed(2)} MB
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                        <Button 
                                            size="small" 
                                            color="error" 
                                            onClick={() => {
                                                if (uploadPreview) URL.revokeObjectURL(uploadPreview);
                                                setUploadFile(null);
                                                setUploadPreview(null);
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>

                        {/* Caption Field */}
                        <Stack direction="column" spacing={1}>
                            <Typography variant="bodyregm" color="var(--main-text-color)">Caption (Optional)</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Add a caption or description for your content..."
                                value={uploadCaption}
                                onChange={(e) => setUploadCaption(e.target.value)}
                                color="secondary"
                                variant="outlined"
                            />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ padding: "16px 24px" }}>
                    <Button 
                        onClick={handleCloseUploadDialog}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleContentUpload}
                        variant="contained"
                        className="gradient-button"
                        disabled={uploading || !uploadFile}
                        startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <IconUpload size="18" />}
                    >
                        {uploading ? "Uploading..." : "Upload Content"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

